import React, { useState } from "react";
import { CalendarCheck, CheckCircle2, Phone, MessageCircle, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, Section, PrimaryButton, SecondaryButton } from "@/components/ui-helpers";
import { AvailabilityPicker } from "@/components/AvailabilityPicker";
import { api } from "@/lib/api";
import { useSite, whatsappLink } from "@/context/SiteContext";
import { toast } from "sonner";

const SERVICES = ["Post-Construction Cleaning", "Move-In / Move-Out Cleaning", "General Cleaning", "General Deep Cleaning", "Property Detailing", "Not sure yet"];
const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-sm text-secondary mb-1.5">{label}</span>{children}</label>
);

export default function RequestTime() {
  const site = useSite();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sel, setSel] = useState({ date: "", slot: "" });
  const [f, setF] = useState({ name: "", phone: "", email: "", service: "", address: "", city: "", notes: "" });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.name || !f.phone) { toast.error("Please enter your name and phone."); return; }
    if (!sel.date || !sel.slot) { toast.error("Please choose a day and time window."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/booking-requests", {
        name: f.name, phone: f.phone, email: f.email, service: f.service,
        date: sel.date, slot: sel.slot, address: f.address, city: f.city, notes: f.notes, source: "request-time",
      });
      if (data.duplicate) {
        toast.info(`You already have a request (${data.ref}) for this window. We'll confirm shortly.`);
      }
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit your request. Please try again.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <>
        <SEO title="Time Requested" />
        <Section className="pt-28">
          <div data-testid="request-time-confirmation" className="ascendia-card max-w-xl mx-auto p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-gold mx-auto" />
            <h1 className="mt-5 font-display text-2xl font-semibold text-white">Window requested, {f.name.split(" ")[0]}!</h1>
            <p className="mt-3 text-secondary">
              Reference <span className="font-mono text-gold-soft">{result.ref}</span>. We've noted your preferred window
              of <span className="text-white">{new Date(result.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span> — {result.slot}.
              Our team will confirm the final time with you shortly.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <PrimaryButton href={whatsappLink(site)} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4" /> Continue on WhatsApp</PrimaryButton>
              <SecondaryButton href={`tel:${site.phone}`}><Phone className="h-4 w-4" /> Call {site.phone}</SecondaryButton>
            </div>
          </div>
        </Section>
      </>
    );
  }

  return (
    <>
      <SEO title="Request a Time" description="See open windows and request a preferred day and time for your Ascendia Detail service. Request only — we confirm the final time with you." />
      <PageHero kicker="Request a Time" title="Find a window that suits you" subtitle="Browse open days and request a preferred time window. It's a request only — we'll confirm the final time with you directly." />
      <Section>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="ascendia-card card-accent-top p-6 sm:p-8">
              <div className="flex items-center gap-2 text-gold-soft mb-5"><CalendarCheck className="h-5 w-5" /><span className="kicker">Open windows</span></div>
              <AvailabilityPicker value={sel} onChange={setSel} />
            </div>
          </div>

          <div className="space-y-5">
            <div className="ascendia-card p-6" data-testid="request-time-form">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Your details</h3>
              <div className="space-y-4">
                <Field label="Full name*"><input className={inputCls} value={f.name} onChange={upd("name")} data-testid="request-time-name" placeholder="Your name" /></Field>
                <Field label="Phone*"><input className={inputCls} value={f.phone} onChange={upd("phone")} data-testid="request-time-phone" placeholder="05x-xxx-xxxx" /></Field>
                <Field label="Email"><input className={inputCls} value={f.email} onChange={upd("email")} placeholder="you@example.com" /></Field>
                <Field label="Service"><select className={inputCls} value={f.service} onChange={upd("service")}><option value="">Select a service</option>{SERVICES.map((s) => <option key={s}>{s}</option>)}</select></Field>
                <Field label="Property address"><input className={inputCls} value={f.address} onChange={upd("address")} data-testid="request-time-address" placeholder="Street & number" /></Field>
                <Field label="City"><input className={inputCls} value={f.city} onChange={upd("city")} data-testid="request-time-city" placeholder="City" /></Field>
                <Field label="Notes"><textarea rows={3} className={inputCls} value={f.notes} onChange={upd("notes")} placeholder="Anything we should know?" /></Field>
              </div>
              <button onClick={submit} disabled={busy} data-testid="request-time-submit" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft transition-colors disabled:opacity-50">
                {busy ? "Submitting…" : "Request this window"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-3 text-xs text-muted-c text-center">Prefer a full quote first? <a href="/contact" className="text-gold-soft hover:text-gold underline">Request a quote</a></p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
